import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  LogOut, User, Clock, FileText, Award, ChevronRight,
  PlayCircle, CheckCircle, AlertCircle, Bell, X, Briefcase, Code, Monitor,
  ShieldCheck, Eye, Timer, Ban, Sparkles, BookOpen, Fingerprint, AlertTriangle, Zap
} from "lucide-react";
import IdentityVerification from "./IdentityVerification";

// ─── Welcome Modal ──────────────────────────────────────────────
const WelcomeModal = ({ userName, examCount, onDismiss }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="welcome-modal">
    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-fade-in">
      {/* Gradient banner */}
      <div className="relative h-44 sm:h-52 bg-gradient-to-br from-indigo-600 via-blue-600 to-violet-600 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="text-center z-10">
          <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-3 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
            <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Welcome aboard!
          </h2>
        </div>
      </div>

      <div className="p-6 sm:p-8">
        <p className="text-lg sm:text-xl font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Hello, {userName}!
        </p>
        <p className="text-sm text-slate-500 mb-5">
          You've been invited to the <span className="font-medium text-indigo-600">edSolutions360</span> Examination Portal.
          {examCount > 0 && <> You have <span className="font-semibold text-slate-800">{examCount} assessment{examCount > 1 ? 's' : ''}</span> waiting for you.</>}
        </p>

        <div className="space-y-2.5 mb-6">
          {[
            { icon: BookOpen, color: 'text-indigo-500 bg-indigo-50', text: 'Adaptive difficulty — questions adjust to your skill level' },
            { icon: Timer, color: 'text-amber-500 bg-amber-50', text: 'Timed assessments — manage your time wisely' },
            { icon: Eye, color: 'text-rose-500 bg-rose-50', text: 'Proctored environment — tab switches are monitored' },
            { icon: Award, color: 'text-emerald-500 bg-emerald-50', text: 'Ranked scoring — top performers get highlighted' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl bg-slate-50/80">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${item.color}`}>
                <item.icon className="w-4 h-4" />
              </div>
              <p className="text-xs sm:text-sm text-slate-600">{item.text}</p>
            </div>
          ))}
        </div>

        <Button
          onClick={onDismiss}
          data-testid="welcome-dismiss-btn"
          className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl text-sm sm:text-base"
        >
          Let's Get Started
          <ChevronRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </div>
  </div>
);

// ─── Exam Rules Modal ───────────────────────────────────────────
const ExamRulesModal = ({ exam, onConfirm, onCancel }) => {
  const [agreed, setAgreed] = useState(false);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 30) setScrolledToEnd(true);
  };

  const rules = [
    { icon: Fingerprint, title: 'Identity Verification', desc: 'You may be required to verify your identity via camera before the exam begins.', severity: 'info' },
    { icon: Timer, title: 'Time Limit', desc: `This exam has a strict time limit of ${exam.duration_minutes} minutes. The timer starts as soon as you begin and cannot be paused.`, severity: 'warning' },
    { icon: FileText, title: 'Question Format', desc: `The exam contains ${exam.num_questions} multiple-choice questions. Difficulty adapts based on your performance.`, severity: 'info' },
    { icon: Ban, title: 'No Tab Switching', desc: 'Switching tabs, opening new windows, or leaving the exam screen is strictly prohibited. All such activities are logged and flagged.', severity: 'critical' },
    { icon: Eye, title: 'Proctoring Active', desc: 'Your session is monitored for suspicious activity including copy-paste attempts, right-clicks, and screen captures.', severity: 'critical' },
    { icon: AlertTriangle, title: 'Single Attempt Only', desc: 'You have only ONE attempt for this exam. Once started, it cannot be restarted or retaken.', severity: 'warning' },
    { icon: Zap, title: 'Auto-Submission', desc: 'If the timer runs out, your exam will be automatically submitted with whatever answers you have provided.', severity: 'warning' },
    { icon: ShieldCheck, title: 'Fair Play Policy', desc: 'Any form of cheating, impersonation, or use of unauthorized resources will result in disqualification and potential blacklisting.', severity: 'critical' },
  ];

  const severityBorder = { info: 'border-l-indigo-400', warning: 'border-l-amber-400', critical: 'border-l-rose-400' };
  const severityIcon = { info: 'text-indigo-500', warning: 'text-amber-500', critical: 'text-rose-500' };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4" data-testid="exam-rules-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="relative px-5 sm:px-8 pt-6 pb-4 flex-shrink-0">
          <button onClick={onCancel} className="absolute top-4 right-4 sm:top-5 sm:right-5 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors" data-testid="rules-close-btn">
            <X className="w-4 h-4 text-slate-500" />
          </button>

          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Examination Rules & Regulations
              </h2>
              <p className="text-xs sm:text-sm text-slate-500">{exam.name}</p>
            </div>
          </div>

          {/* Exam meta */}
          <div className="flex flex-wrap gap-2 sm:gap-3 mt-2">
            {[
              { icon: FileText, label: `${exam.num_questions} Questions` },
              { icon: Clock, label: `${exam.duration_minutes} Minutes` },
              { icon: Award, label: `Passing: ${exam.passing_score}%` },
            ].map((m, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 rounded-lg text-xs text-slate-600">
                <m.icon className="w-3 h-3" />{m.label}
              </span>
            ))}
          </div>
        </div>

        {/* Scrollable rules */}
        <div
          className="flex-1 overflow-y-auto px-5 sm:px-8 pb-2"
          onScroll={handleScroll}
          style={{ WebkitOverflowScrolling: 'touch' }}
          data-testid="rules-scroll-area"
        >
          <div className="space-y-2.5 sm:space-y-3 pb-2">
            {rules.map((rule, i) => (
              <div
                key={i}
                className={`flex items-start gap-3 p-3 sm:p-3.5 bg-slate-50 rounded-xl border-l-[3px] ${severityBorder[rule.severity]}`}
                data-testid={`rule-${i}`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  <rule.icon className={`w-4 h-4 ${severityIcon[rule.severity]}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800">{rule.title}</p>
                  <p className="text-xs sm:text-sm text-slate-500 leading-relaxed mt-0.5">{rule.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Warning banner */}
          <div className="my-4 p-3.5 sm:p-4 bg-rose-50 border border-rose-200 rounded-xl" data-testid="violation-warning">
            <div className="flex items-start gap-2.5">
              <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-rose-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-rose-800">Violation Consequences</p>
                <p className="text-xs sm:text-sm text-rose-600 mt-1 leading-relaxed">
                  Any violation of the above rules may result in immediate exam termination, score invalidation, and reporting to the examination authority.
                  Your session data, IP address, and activity logs are recorded for audit purposes.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer — agreement + confirm */}
        <div className="flex-shrink-0 border-t border-slate-200 px-5 sm:px-8 py-4 sm:py-5 bg-slate-50/80">
          <label className="flex items-start gap-3 cursor-pointer mb-4" data-testid="agree-checkbox-label">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 w-4 h-4 sm:w-5 sm:h-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
              data-testid="agree-checkbox"
            />
            <span className="text-xs sm:text-sm text-slate-700 leading-relaxed">
              I have read and understood all the rules and regulations above. I agree to abide by them and accept the consequences of any violations.
            </span>
          </label>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onCancel}
              data-testid="rules-cancel-btn"
              className="flex-1 py-2.5 sm:py-3 rounded-xl text-sm"
            >
              Go Back
            </Button>
            <Button
              onClick={onConfirm}
              disabled={!agreed}
              data-testid="rules-confirm-btn"
              className="flex-1 py-2.5 sm:py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl text-sm transition-all"
            >
              Begin Examination
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Dashboard ─────────────────────────────────────────────
const CandidateDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);
  const [rulesExam, setRulesExam] = useState(null);
  const [pendingVerification, setPendingVerification] = useState(null);
  const [verificationChecked, setVerificationChecked] = useState(false);

  useEffect(() => { fetchData(); checkPendingVerification(); }, []);

  // Check first-login flag after data loads AND verification is done
  useEffect(() => {
    if (!loading && user && verificationChecked && !pendingVerification) {
      const key = `edsol360_welcomed_${user.id}`;
      if (!localStorage.getItem(key)) {
        setShowWelcome(true);
        localStorage.setItem(key, '1');
      }
    }
  }, [loading, user, verificationChecked, pendingVerification]);

  const checkPendingVerification = async () => {
    try {
      const { data } = await axios.get(`${API}/candidate/pending-verification`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data.requires_verification) {
        setPendingVerification(data);
      }
    } catch (e) { /* no verification needed */ }
    finally { setVerificationChecked(true); }
  };

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [examsRes, notifRes] = await Promise.all([
        axios.get(`${API}/candidate/exams`, { headers }),
        axios.get(`${API}/candidate/notifications`, { headers }).catch(() => ({ data: [] }))
      ]);
      setExams(examsRes.data);
      setNotifications(notifRes.data);
    } catch (error) {
      toast.error("Failed to fetch exams");
    } finally {
      setLoading(false);
    }
  };

  const dismissNotification = async (id) => {
    try {
      await axios.put(`${API}/candidate/notifications/${id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (e) { /* ignore */ }
  };

  const dismissAllNotifications = async () => {
    try {
      await axios.put(`${API}/candidate/notifications/read-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications([]);
    } catch (e) { /* ignore */ }
  };

  const handleStartExam = (exam) => {
    // Show rules modal for not_started exams
    setRulesExam(exam);
  };

  const handleConfirmStart = () => {
    if (rulesExam) {
      navigate(`/exam/${rulesExam.id}`);
      setRulesExam(null);
    }
  };

  const handleResumeExam = (examId) => navigate(`/exam/${examId}`);
  const handleLogout = () => { logout(); navigate("/auth"); toast.success("Logged out successfully"); };

  const getStatusBadge = (status) => {
    const config = {
      not_started: { label: "Not Started", class: "status-not-started", icon: FileText },
      in_progress: { label: "In Progress", class: "status-in-progress", icon: PlayCircle },
      completed: { label: "Submitted", class: "status-completed", icon: CheckCircle },
      time_expired: { label: "Submitted", class: "status-completed", icon: CheckCircle }
    };
    const { label, class: className, icon: Icon } = config[status] || config.not_started;
    return <span className={`inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${className}`}><Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />{label}</span>;
  };

  const unreadNotifs = notifications.filter(n => !n.is_read);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="candidate-dashboard">
      {/* Face Verification Screen - shown on first login if photo exists */}
      {pendingVerification && (
        <IdentityVerification
          examId={pendingVerification.exam_id}
          examName={pendingVerification.exam_name}
          onVerified={() => {
            setPendingVerification(null);
            toast.success("Identity verified! Welcome to the portal.");
          }}
          onSkip={() => setPendingVerification(null)}
        />
      )}

      {/* Welcome Modal - shown after verification is done */}
      {!pendingVerification && showWelcome && (
        <WelcomeModal
          userName={user?.full_name?.split(' ')[0] || 'Candidate'}
          examCount={exams.length}
          onDismiss={() => setShowWelcome(false)}
        />
      )}

      {/* Exam Rules Modal */}
      {rulesExam && (
        <ExamRulesModal
          exam={rulesExam}
          onConfirm={handleConfirmStart}
          onCancel={() => setRulesExam(null)}
        />
      )}

      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5 sm:gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-blue-500 flex items-center justify-center">
              <Award className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <div>
              <h1 className="text-base sm:text-xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>edSolutions360</h1>
              <p className="text-[10px] sm:text-xs text-slate-500">Assessment Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="hidden sm:flex items-center gap-3 px-4 py-2 bg-slate-50 rounded-xl">
              <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center"><User className="w-5 h-5 text-indigo-600" /></div>
              <div>
                <p className="text-sm font-medium text-slate-900">{user?.full_name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
            </div>
            <div className="sm:hidden flex items-center gap-2 px-2.5 py-1.5 bg-slate-50 rounded-lg">
              <div className="w-7 h-7 rounded-md bg-indigo-100 flex items-center justify-center"><User className="w-3.5 h-3.5 text-indigo-600" /></div>
              <p className="text-xs font-medium text-slate-900 max-w-[100px] truncate">{user?.full_name}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} className="text-slate-500 hover:text-slate-900 w-8 h-8 sm:w-10 sm:h-10" data-testid="logout-btn">
              <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-8">
        {/* Notification Banners */}
        {unreadNotifs.length > 0 && (
          <div className="mb-5 sm:mb-6 space-y-2.5 sm:space-y-3 animate-fade-in" data-testid="notifications-section">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-indigo-600" />
                <span className="text-xs sm:text-sm font-medium text-slate-700">{unreadNotifs.length} Notification{unreadNotifs.length > 1 ? 's' : ''}</span>
              </div>
              {unreadNotifs.length > 1 && (
                <Button variant="ghost" size="sm" onClick={dismissAllNotifications} className="text-xs text-slate-500">Dismiss All</Button>
              )}
            </div>
            {unreadNotifs.map((notif) => (
              <div key={notif.id} className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 sm:p-4 flex items-start justify-between" data-testid={`notification-${notif.id}`}>
                <div className="flex items-start gap-2.5 sm:gap-3">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-indigo-100 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <Bell className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-indigo-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-indigo-900 text-xs sm:text-sm">{notif.title}</p>
                    <p className="text-xs sm:text-sm text-indigo-700">{notif.message}</p>
                    <p className="text-[10px] sm:text-xs text-indigo-500 mt-1">{new Date(notif.created_at).toLocaleString()}</p>
                  </div>
                </div>
                <button onClick={() => dismissNotification(notif.id)} className="text-indigo-400 hover:text-indigo-600 p-1 flex-shrink-0">
                  <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Welcome Section */}
        <div className="mb-6 sm:mb-8 animate-fade-in">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1 sm:mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Welcome, {user?.full_name?.split(' ')[0]}!</h2>
          <p className="text-sm sm:text-base text-slate-600">Here are the assessments assigned to you. Complete them to showcase your skills.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 sm:gap-6 mb-6 sm:mb-8 stagger-children">
          <Card className="stats-card">
            <div className="flex items-center justify-between p-3 sm:p-5">
              <div><p className="text-[10px] sm:text-sm text-slate-500 mb-0.5 sm:mb-1">Total Exams</p><p className="text-xl sm:text-3xl font-bold text-slate-900">{exams.length}</p></div>
              <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-indigo-50 flex items-center justify-center"><FileText className="w-4 h-4 sm:w-6 sm:h-6 text-indigo-600" /></div>
            </div>
          </Card>
          <Card className="stats-card">
            <div className="flex items-center justify-between p-3 sm:p-5">
              <div><p className="text-[10px] sm:text-sm text-slate-500 mb-0.5 sm:mb-1">Completed</p><p className="text-xl sm:text-3xl font-bold text-emerald-600">{exams.filter(e => e.status === "completed" || e.status === "time_expired").length}</p></div>
              <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-emerald-50 flex items-center justify-center"><CheckCircle className="w-4 h-4 sm:w-6 sm:h-6 text-emerald-600" /></div>
            </div>
          </Card>
          <Card className="stats-card">
            <div className="flex items-center justify-between p-3 sm:p-5">
              <div><p className="text-[10px] sm:text-sm text-slate-500 mb-0.5 sm:mb-1">Pending</p><p className="text-xl sm:text-3xl font-bold text-amber-600">{exams.filter(e => e.status === "not_started" || e.status === "in_progress").length}</p></div>
              <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-amber-50 flex items-center justify-center"><Clock className="w-4 h-4 sm:w-6 sm:h-6 text-amber-600" /></div>
            </div>
          </Card>
        </div>

        {/* Exams List */}
        <div className="animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mb-3 sm:mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Your Assessments</h3>
          {loading ? (
            <div className="flex items-center justify-center py-12"><div className="spinner" /></div>
          ) : exams.length === 0 ? (
            <Card className="text-center py-10 sm:py-12">
              <FileText className="w-12 h-12 sm:w-16 sm:h-16 text-slate-300 mx-auto mb-3 sm:mb-4" />
              <h4 className="text-base sm:text-lg font-medium text-slate-900 mb-2">No Exams Assigned</h4>
              <p className="text-sm text-slate-500">You don't have any exams assigned yet.</p>
            </Card>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {exams.map((examData) => {
                const { exam, status, attempt } = examData;
                const RoleIcon = exam.role === "BA" ? Briefcase : exam.role === "Frontend" ? Monitor : Code;
                return (
                  <Card key={exam.id} className="card-hover" data-testid={`exam-card-${exam.id}`}>
                    <CardContent className="p-4 sm:p-6">
                      {/* Mobile: stacked layout */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                        <div className="flex items-center gap-3 sm:gap-4">
                          <div className={`w-11 h-11 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${exam.role === "BA" ? "bg-amber-50" : exam.role === "Frontend" ? "bg-emerald-50" : "bg-blue-50"}`}>
                            <RoleIcon className={`w-5 h-5 sm:w-7 sm:h-7 ${exam.role === "BA" ? "text-amber-600" : exam.role === "Frontend" ? "text-emerald-600" : "text-blue-600"}`} />
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-sm sm:text-lg font-semibold text-slate-900 mb-0.5 sm:mb-1 truncate">{exam.name}</h4>
                            <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-slate-500">
                              <span className="flex items-center gap-1"><FileText className="w-3 h-3 sm:w-4 sm:h-4" />{exam.num_questions} Qs</span>
                              <span className="flex items-center gap-1"><Clock className="w-3 h-3 sm:w-4 sm:h-4" />{exam.duration_minutes} Min</span>
                              <Badge variant="outline" className="text-[10px] sm:text-xs py-0">{exam.role === "BA" ? "Business Analyst" : exam.role === "Frontend" ? "Frontend Dev" : "MERN Dev"}</Badge>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4">
                          {getStatusBadge(status)}
                          {status === "not_started" && (
                            <Button onClick={() => handleStartExam(exam)} className="bg-indigo-600 hover:bg-indigo-700 btn-active text-xs sm:text-sm px-3 sm:px-4" data-testid={`start-exam-btn-${exam.id}`}>
                              Start Exam<ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 ml-1" />
                            </Button>
                          )}
                          {status === "in_progress" && (
                            <Button onClick={() => handleResumeExam(exam.id)} className="bg-blue-600 hover:bg-blue-700 btn-active text-xs sm:text-sm px-3 sm:px-4" data-testid={`resume-exam-btn-${exam.id}`}>
                              Resume<ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 ml-1" />
                            </Button>
                          )}
                          {(status === "completed" || status === "time_expired") && (
                            <div className="flex items-center gap-1.5 sm:gap-2 text-emerald-600 bg-emerald-50 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg">
                              <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" /><span className="text-xs sm:text-sm font-medium">Awaiting Results</span>
                            </div>
                          )}
                        </div>
                      </div>
                      {attempt && (status === "completed" || status === "time_expired") && (
                        <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-slate-100">
                          <p className="text-xs sm:text-sm text-slate-600">Your exam has been submitted. Shortlisting results will be announced shortly.</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        <Card className="mt-6 sm:mt-8 bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-100">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0"><AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" /></div>
              <div>
                <h4 className="font-semibold text-slate-900 text-sm sm:text-base mb-1 sm:mb-2">Important Guidelines</h4>
                <ul className="text-xs sm:text-sm text-slate-600 space-y-0.5 sm:space-y-1">
                  <li>Each exam can only be attempted once</li>
                  <li>Do not switch tabs or windows during the exam</li>
                  <li>Exam will auto-submit when time expires</li>
                  <li>Questions adapt to your performance level</li>
                  <li>All activities are monitored for integrity</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CandidateDashboard;
